import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { environment } from '@env';
import { NotificationQueryEnum } from '@models';
import { StringFns } from '@utils';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';

export interface TemplatePreviewDialogParams {
    /** Html with placeholder tokens */
    template: string;
    /** Whether to convert html to plain text */
    isText: boolean;
    queryId: NotificationQueryEnum;
    /** json e.g. "{ accountId: 123, jobOrderId: 456 }" */
    context: string;
    /** When true, wrap the merged HTML with Email Header and Footer templates */
    wrapWithHeaderFooter?: boolean;
}

@Component({
    selector: 'prox-template-preview-dialog',
    template: `
  <div class="h-full flex flex-col" >
    <div class="grow overflow-auto border rounded p-4" style="font-family: Arial, sans-serif;">
      <div class="email-preview-content" [innerHTML]="content"></div>
    </div>
    <div class="text-right pt-2">
      <button (click)="close()" pButton type="button" icon="pi pi-times" label="Close" class="p-button-raised p-button-sm"></button>
    </div>
  </div>`,
    styles: [`
      .email-preview-content ::ng-deep p { margin: 0 0 1em 0; }
      .email-preview-content ::ng-deep p:empty { min-height: 1em; }
      .email-preview-content ::ng-deep hr { border: none; border-top: 1px solid #ccc; margin: 1em 0; }
      .email-preview-content ::ng-deep h1, .email-preview-content ::ng-deep h2, .email-preview-content ::ng-deep h3 { margin: 0.5em 0; }
      .email-preview-content ::ng-deep table { border-collapse: collapse; width: 100%; margin: 1em 0; }
      .email-preview-content ::ng-deep th, .email-preview-content ::ng-deep td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
      .email-preview-content ::ng-deep th { background-color: #f5f5f5; font-weight: bold; }
    `],
    standalone: false
})
export class TemplatePreviewDialogComponent implements OnInit {

  /** html with values merged */
  content?: SafeHtml;
  data: TemplatePreviewDialogParams;

  constructor(dialogConfig: DynamicDialogConfig, private ref: DynamicDialogRef, private domSanitizer: DomSanitizer, private httpClient: HttpClient) {
    this.data = dialogConfig.data;
  }

  public static async openDialog(dialogService: DialogService, data: TemplatePreviewDialogParams, config?: DynamicDialogConfig<TemplatePreviewDialogParams>) {
    const ref = dialogService.open(TemplatePreviewDialogComponent, {
      closable: false,
      draggable: true,
      width: '780px',
      height: '70vh',
      focusOnShow: false,
      ...config,
      data: data,
    });
    return firstValueFrom(ref.onClose);
  }

  async ngOnInit(): Promise<void> {
    this.showContent();
  }

  async showContent(): Promise<void> {
    const url = environment.accountUrl + (this.data.isText ? '/MergeTemplateText': '/MergeTemplateHtml');
    const body = {
      template: this.data.template,
      notificationQueryId: this.data.queryId,
      contextJson: this.data.context,
      wrapWithHeaderFooter: this.data.wrapWithHeaderFooter ?? false,
    };
    try {
      const merged = await firstValueFrom(this.httpClient.post<{Value: string}>(url, body));
      const content = merged ? StringFns.purify(merged.Value, { ADD_TAGS: ['style'], FORCE_BODY: true }) : 'no content';
      this.content = this.domSanitizer.bypassSecurityTrustHtml(content);
    } catch {
      this.content = 'Unable to preview';
    }
  }

  close() {
    this.ref.close();
  }

}
